import { Box } from "@mui/material";
import { ChartData, ChartDataset } from "chart.js";
import { t } from "i18next";
import { Bar } from "react-chartjs-2";
import { useTheme } from "../../context/ThemeContext";
import { Currency } from "../../i18n/Currency";
import { Stage } from "../../shared/CaseConfiguration";
import { StageRevenue } from "../../shared/CaseModel";
import {
  getLegendLabels,
  getTicks,
  getTooltip,
  wrapLabel,
} from "../../utils/chartjsTheming";
import { revenueColors } from "./revenueParts";

export type { StageRevenue };

/** The height of the chart, in px. */
const CHART_HEIGHT = 280;

/** The bar of a stage is no wider than this, in px. */
const MAX_BAR_THICKNESS = 88;

export const RevenueBarChart = ({
  data,
  currency,
}: {
  data: StageRevenue[];
  currency: Currency;
}) => {
  const { theme } = useTheme();
  const colors = revenueColors(theme);

  const hasCfaSuccessFees = data.some((d) => d.cfaSuccessFee > 0);
  const hasCfaNoWinFees = data.some((d) => d.cfaNoWinFee > 0);

  // what the firm is left with is the result of the case and no part of a
  // stage: the figures over the chart state it, the bars do not
  const datasets: ChartDataset<"bar", number[]>[] = [
    {
      label: t("case.feeStructure.indirectCosts"),
      data: data.map((stageCosts) => stageCosts.indirectCosts),
      backgroundColor: colors.indirectCosts.color,
    },
    {
      label: t("case.feeStructure.directCosts"),
      data: data.map((stageCosts) => stageCosts.directCosts),
      backgroundColor: colors.directCosts.color,
    },
  ];

  if (hasCfaSuccessFees) {
    datasets.push({
      label: t("case.analytics.revenue.cfa.successFee"),
      data: data.map((d) => d.cfaSuccessFee),
      backgroundColor: colors.cfaFees.color,
    });
  }

  if (hasCfaNoWinFees) {
    datasets.push({
      label: t("case.analytics.revenue.cfa.noWinFee"),
      data: data.map((d) => d.cfaNoWinFee),
      backgroundColor: colors.cfaNoWinFee.color,
    });
  }

  const chartData: ChartData<"bar", number[]> = {
    labels: data.map((stageCosts) =>
      wrapLabel(t("case.stages." + Stage[stageCosts.stage])),
    ),
    datasets,
  };

  // the figures over the chart say which colour is which, but for the two
  // kinds of CFA fees
  const showLegend = hasCfaSuccessFees || hasCfaNoWinFees;

  return (
    <Box sx={{ height: CHART_HEIGHT }}>
      <Bar
        data={chartData}
        options={{
          // the chart fills the frame it is given rather than keeping a shape
          maintainAspectRatio: false,
          // a case with few stages does not get bars as wide as they could be
          datasets: { bar: { maxBarThickness: MAX_BAR_THICKNESS } },
          interaction: {
            mode: "index",
          },
          plugins: {
            legend: {
              display: showLegend,
              position: "bottom",
              align: "start",
              labels: {
                ...getLegendLabels(theme),
                boxWidth: 12,
                boxHeight: 12,
                padding: 16,
              },
            },
            tooltip: {
              ...getTooltip(theme),
              callbacks: {
                // no sum of a stage's rows: fees less costs would be a result,
                // and the result is that of the case, not of a stage
                label: (item) =>
                  `${item.dataset.label}: ${currency.format(item.parsed.y ?? 0)}`,
              },
              itemSort: (a, b) => b.datasetIndex - a.datasetIndex,
            },
          },
          scales: {
            y: {
              ticks: {
                ...getTicks(theme),
                callback: function (value) {
                  return typeof value === "number"
                    ? currency.formatCompact(value)
                    : value;
                },
              },
              grid: {
                // the line of zero stands out from the lines of the other amounts
                color: (context) =>
                  context.tick.value === 0
                    ? theme.palette.text.disabled
                    : theme.palette.divider,
              },
              stacked: true,
            },
            x: {
              ticks: { ...getTicks(theme), maxRotation: 0, autoSkip: false },
              grid: { display: false },
              stacked: true,
            },
          },
        }}
      />
    </Box>
  );
};
